<template>
  <el-dialog
    title="创建任务组"
    :visible.sync="dialogVisible"
    width="1050px"
    :close-on-click-modal="false"
    append-to-body
    class="create-task-dialog"
    top="5vh"
    @open="handleOpen"
  >
    <!-- 步骤条 -->
    <el-steps
      :active="activeStep"
      finish-status="success"
      simple
      style="margin-bottom: 15px"
    >
      <el-step title="基本信息" icon="el-icon-edit"/>
      <el-step title="详细配置" icon="el-icon-s-operation"/>
      <el-step title="生成预览" icon="el-icon-view"/>
    </el-steps>

    <!-- 步骤内容 -->
    <div v-loading="previewLoading" class="step-content">
      <!-- 第一步：基本信息填写 -->
      <Step1BasicInfo
        v-show="activeStep === 0"
        ref="step1"
        :form-data="formData"
        :area-tree="areaTree"
        :panel-active="dialogVisible"
        @cycle-type-change="handleCycleTypeChange"
        @task-year-change="handleTaskYearChange"
        @zone-change="handleZoneChange"
        @auto-name="handleAutoName"
        @navigate-to-team="handleNavigateToTeam"
        @navigate-to-org-user="handleNavigateToOrgUser"
      />

      <!-- 第二步：具体任务详细配置 -->
      <Step2DetailConfig
        v-show="activeStep === 1"
        ref="step2"
        :form-data="formData"
        :area-tree="areaTree"
        :system-list="systemList"
        :type-list="typeList"
        @update:period-configs="onPeriodConfigsUpdate"
        @reset-to-single-period="handleResetToSinglePeriod"
      />

      <!-- 第三步：生成预览 -->
      <Step3Preview
        v-show="activeStep === 2"
        :preview-data="previewData"
        @warning-change="onPreviewWarningChange"
      />
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button v-if="activeStep > 0" type="primary" plain class="step-prev-button" @click="prevStep">上一步</el-button>
      <el-button v-if="activeStep < 2" type="primary" @click="nextStep">下一步</el-button>
      <el-button v-if="activeStep === 2" type="primary" :loading="submitting" :disabled="!canSubmit" @click="handleSubmit">确认生成</el-button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * 创建任务组弹窗（三步：基本信息 → 详细配置 → 生成预览）
 * 负责跨步骤共享的 formData、字典数据传递、预览与提交；
 * 各步骤内部的 UI 状态、派生计算和交互操作由步骤组件自行管理。
 */

// 服务
import { taskService } from '../task.service';
// 工具函数
import {
  generatePeriodName,
  normalizeDateToTaskYear,
  getDefaultPeriodStartDate,
  buildMonthlySplitPeriodConfigs
} from '../utils/dateCalc';
import { collectAllAreaIds, getZoneNameFromTree } from '../utils/areaHandler';
import {
  getCycleTypeLabel,
  getTaskTypeLabel,
  isCycleWithoutTaskYear,
  isYearSplitMonthlyCycle,
  DEFAULT_TASK_TYPE,
  DEFAULT_CYCLE_TYPE
} from '@/features/gnmk/constants/taskCycleEnums';
// 步骤组件
import Step1BasicInfo from './Step1BasicInfo.vue';
import Step2DetailConfig from './Step2DetailConfig.vue';
import Step3Preview from './Step3Preview.vue';

export default {
  name: 'TaskGroupDialog',
  components: {
    Step1BasicInfo,
    Step2DetailConfig,
    Step3Preview
  },
  props: {
    /** 弹窗显示，配合 .sync 使用 update:visible */
    visible: {
      type: Boolean,
      default: false
    },
    /** 区域树：大区选择与子区域级联的数据源 */
    areaTree: {
      type: Array,
      default: () => []
    },
    /** 消防系统字典，用于第二步多选与名称展示 */
    systemList: {
      type: Array,
      default: () => []
    },
    /** 设施类型列表，按系统 id 过滤 */
    typeList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    const currentYear = new Date().getFullYear();
    return {
      /** 当前步骤索引：0 基本信息 / 1 详细配置 / 2 预览 */
      activeStep: 0,
      /** 任务组表单主体，含周期配置 periodConfigs（每期含 systemConfigs） */
      formData: {
        groupName: null,
        taskType: DEFAULT_TASK_TYPE,
        cycleType: DEFAULT_CYCLE_TYPE,
        taskYear: currentYear,
        zoneAreaId: null,
        assignType: 'none',
        assignUserIds: [],
        assignTeamId: null,
        isSplitMonthly: false,
        periodConfigs: []
      },
      /** 第三步预览接口加载中 */
      previewLoading: false,
      /** 预览接口返回的数据（汇总与子任务列表） */
      previewData: null,
      /** 确认生成提交中 */
      submitting: false,
      /** Step3 告警状态，由 @warning-change 事件更新，用于 canSubmit 判断 */
      hasTypeWarning: false
    };
  },
  computed: {
    /** 与父组件 visible 双向绑定，关闭弹窗时同步到父级 */
    dialogVisible: {
      get () {
        return this.visible;
      },
      set (val) {
        this.$emit('update:visible', val);
      }
    },
    /** 预览中是否存在「有设备但无检查项」的子任务 */
    hasEmptyCheckContent () {
      return this.previewData &&
        this.previewData.subTasks &&
        this.previewData.subTasks.some(t => t.deviceCount > 0 && !t.checkContentCount);
    },
    /** 是否允许点击「确认生成」：有预览数据、有设备与子任务、无空检查项、无类型告警 */
    canSubmit () {
      if (!this.previewData) return false;
      if (!this.previewData.totalDevices) return false;
      if (!this.previewData.totalSubTasks) return false;
      if (this.hasEmptyCheckContent) return false;
      if (this.hasTypeWarning) return false;
      return true;
    }
  },
  methods: {
    /** 弹窗打开：重置步骤与表单，并按周期类型初始化期次 */
    handleOpen () {
      this.activeStep = 0;
      this.resetFormData();
      this.handleCycleTypeChange();
    },
    /** 恢复表单与预览状态的初始值 */
    resetFormData () {
      const currentYear = new Date().getFullYear();
      this.formData = {
        groupName: null,
        taskType: DEFAULT_TASK_TYPE,
        cycleType: DEFAULT_CYCLE_TYPE,
        taskYear: currentYear,
        zoneAreaId: null,
        assignType: 'none',
        assignUserIds: [],
        assignTeamId: null,
        isSplitMonthly: false,
        periodConfigs: []
      };
      this.previewData = null;
      this.hasTypeWarning = false;
    },

    /** 步骤一引导：前往人员小组管理，先关弹窗再跳转，避免遮罩盖住目标页 */
    handleNavigateToTeam () {
      this.dialogVisible = false;
      this.$router.push({ name: 'teamList' }).catch(() => {});
    },

    /** 步骤一引导：前往组织人员管理维护可指派的执行人 */
    handleNavigateToOrgUser () {
      this.dialogVisible = false;
      this.$router.push({ name: 'orgUserList' }).catch(() => {});
    },

    /**
     * 第一步 任务年份变更：规范化数字、刷新自动命名；
     * 若已按月分解则重算各月期次，否则清空并新增默认起始期次。
     */
    handleTaskYearChange () {
      // 选择的周期类型是否为不按任务年份约束：每日、专项
      const isWithoutTaskYear = isCycleWithoutTaskYear(this.formData.cycleType);
      if (!isWithoutTaskYear && this.formData.taskYear != null && this.formData.taskYear !== '') {
        const raw = this.formData.taskYear;
        const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
        if (Number.isFinite(n)) {
          this.formData.taskYear = n;
        }
      }
      this.handleAutoName();
      if (isWithoutTaskYear) return;
      // 选择的周期类型是否支持按月分解：年度、年度日查
      const isSplitMonthly = this.formData.isSplitMonthly && isYearSplitMonthlyCycle(this.formData.cycleType);
      if (isSplitMonthly) {
        // 按月分解：重新计算各月期次
        const list = buildMonthlySplitPeriodConfigs(this.formData.taskYear);
        if (list) this.formData.periodConfigs = list;
      } else {
        // 不按月分解：清空期次并新增默认起始期次
        this.formData.periodConfigs = [];
        this._addDefaultPeriod();
      }
    },

    /**
     * 第一步 执行周期变更：重置任务年份与按月分解状态，重建期次列表。
     */
    handleCycleTypeChange () {
      if (!isYearSplitMonthlyCycle(this.formData.cycleType)) {
        this.formData.isSplitMonthly = false;
      }
      if (isCycleWithoutTaskYear(this.formData.cycleType)) {
        this.formData.taskYear = null;
      } else if (!this.formData.taskYear) {
        this.formData.taskYear = new Date().getFullYear();
      }
      this.formData.periodConfigs = [];
      if (this.formData.isSplitMonthly && isYearSplitMonthlyCycle(this.formData.cycleType)) {
        const list = buildMonthlySplitPeriodConfigs(this.formData.taskYear);
        if (list) this.formData.periodConfigs = list;
      } else {
        this._addDefaultPeriod();
      }
      this.handleAutoName();
    },

    /**
     * 第一步 大区变更：清空各期次下系统已选子区域，避免跨区脏数据
     */
    handleZoneChange () {
      this.formData.periodConfigs.forEach(period => {
        period.systemConfigs.forEach(sys => { sys.areaIds = []; });
      });
      this.handleAutoName();
    },

    /**
     * 第一步 按大区、年份、周期、任务类型拼接任务组默认名称
     */
    handleAutoName () {
      const zoneName = this.getZoneName();
      const cycleText = getCycleTypeLabel(this.formData.cycleType);
      const typeText = getTaskTypeLabel(this.formData.taskType);
      if (!zoneName) return;
      if (isCycleWithoutTaskYear(this.formData.cycleType)) {
        this.formData.groupName = `${zoneName} · ${cycleText} · ${typeText}`;
      } else if (this.formData.taskYear) {
        this.formData.groupName = `${zoneName} · ${this.formData.taskYear}年 · ${cycleText} · ${typeText}`;
      }
    },

    /** 根据所选大区 id 从区域树解析展示名称 */
    getZoneName () {
      return getZoneNameFromTree(this.areaTree, this.formData.zoneAreaId);
    },

    /**
     * 第二步 按月分解关闭时 emit reset-to-single-period，
     * 在此恢复为单期默认，第二步的 watcher 负责重置选中下标。
     */
    handleResetToSinglePeriod () {
      this.formData.periodConfigs = [];
      this._addDefaultPeriod();
    },

    /**
     * 第二步 在需要整体替换 periodConfigs 时（如开启按月分解）emit update:period-configs，
     * 在此更新 formData，第二步的 watcher 负责重置选中下标。
     */
    onPeriodConfigsUpdate (newConfigs) {
      this.formData.periodConfigs = newConfigs;
    },

    /** 第三步 预览数据变化后 emit warning-change，同步告警状态供 canSubmit 使用 */
    onPreviewWarningChange ({ hasDeviceTypeWarning, hasCheckTypeWarning }) {
      this.hasTypeWarning = hasDeviceTypeWarning || hasCheckTypeWarning;
    },

    /**
     * 初始化/重置流程中添加单条默认期次。
     * 与第二步的交互式 addPeriod 分开，避免循环依赖：此处只负责重置时填充第一期。
     */
    _addDefaultPeriod () {
      const base = getDefaultPeriodStartDate(this.formData.cycleType, this.formData.taskYear);
      const targetDate = normalizeDateToTaskYear(new Date(base), this.formData.cycleType, this.formData.taskYear);
      this.formData.periodConfigs.push({
        periodName: generatePeriodName(targetDate, this.formData.cycleType),
        executeDate: targetDate,
        deadlineDate: null,
        systemConfigs: []
      });
    },

    /**
     * 下一步：第一步校验表单并查重；第二步校验每期已配系统后拉预览并进入第三步。
     */
    nextStep () {
      if (this.activeStep === 0) {
        // 第一步：校验表单并查重
        this.$refs.step1.validate().then((valid) => {
          if (!valid) return;
          this.checkDuplicate().then(isDuplicate => {
            if (isDuplicate) {
              const cycleText = getCycleTypeLabel(this.formData.cycleType);
              const typeText = getTaskTypeLabel(this.formData.taskType);
              this.$alert(
                `已存在相同任务类型（${typeText}）、执行周期（${cycleText}）和执行大区（${this.getZoneName()}）的任务组，请重新选择配置`,
                '重复提示',
                { type: 'warning' }
              ).then(() => {}).catch(() => {});
            } else {
              this.activeStep++;
            }
          });
        });
      } else if (this.activeStep === 1) {
        // 第二步：校验每期已配系统后拉取预览数据并进入第三步
        if (this.formData.periodConfigs.length === 0) {
          this.$message.warning('请至少配置一个周期');
          return;
        }
        const invalidPeriod = this.formData.periodConfigs.find(p => p.systemConfigs.length === 0);
        if (invalidPeriod) {
          this.$message.warning(`"${invalidPeriod.periodName}"未配置任何消防系统`);
          return;
        }
        this.fetchPreview();
        this.activeStep++;
      }
    },

    /**
     * 查询是否已存在相同任务类型、执行周期、年份与大区的任务组（第一步防重复）
     * @returns {Promise<boolean>} 存在重复则为 true
     */
     async checkDuplicate () {
      try {
        const res = await taskService.queryTaskGroupPage({
          taskType: this.formData.taskType,
          cycleType: this.formData.cycleType,
          taskYear: this.formData.taskYear || '',
          pageNo: 1,
          pageSize: 20
        });
        const list = res.data.list || res.data.rows || [];
        return list.some(g => g.zoneAreaId === this.formData.zoneAreaId);
      } catch {
        return false;
      }
    },

    /** 返回上一步 */
    prevStep () {
      if (this.activeStep > 0) this.activeStep--;
    },

    /**
     * 组装提交/预览用的载荷：子区域 id 展开为全路径、人员 id 拼接、分配字段清理、按月分解转 Y/N
     */
    buildSubmitData () {
      // 基础数据配置
      const formCopy = JSON.parse(JSON.stringify(this.formData));
      formCopy.periodConfigs.forEach(period => {
        period.systemConfigs.forEach(sysConfig => {
          if (sysConfig.areaIds && sysConfig.areaIds.length > 0) {
            sysConfig.areaIds = collectAllAreaIds(sysConfig.areaIds, this.areaTree);
          }
        });
      });
      // 分配人员信息
      if (formCopy.assignType !== 'user') {
        formCopy.assignUserIds = null;
      } else {
        const userIds = formCopy.assignUserIds || [];
        formCopy.assignUserIds = userIds.length > 0 ? userIds.join(',') : null;
      }
      if (formCopy.assignType !== 'team') {
        formCopy.assignTeamId = null;
      }
      // 是否支持按月拆解的周期：年度、年度日查
      const showSplit = isYearSplitMonthlyCycle(formCopy.cycleType);
      const on = formCopy.isSplitMonthly === true || formCopy.isSplitMonthly === 'Y';
      formCopy.isSplitMonthly = showSplit && on ? 'Y' : 'N';
      return { ...formCopy };
    },

    /** 第三步 调用预览接口填充 previewData */
    fetchPreview () {
      this.previewLoading = true;
      const submitData = this.buildSubmitData();
      taskService.previewTaskGroup(submitData).then(res => {
        this.previewData = res.data;
      }).finally(() => {
        this.previewLoading = false;
      });
    },

    /** 第三步 确认生成任务组，成功后关闭弹窗并通知父级刷新 */
    handleSubmit () {
      this.submitting = true;
      const submitData = this.buildSubmitData();
      taskService.createTaskGroup(submitData).then(() => {
        this.$message.success('任务创建成功');
        this.dialogVisible = false;
        this.$emit('refresh');
      }).finally(() => {
        this.submitting = false;
      });
    }
  }
};
</script>

<style scoped lang="scss">
.step-prev-button:not(.is-disabled) {
  color: #409eff;
  border-color: #409eff;
  background: transparent;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.create-task-dialog {
  .step-content {
    min-height: 500px;
  }
}
</style>
